import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';

const ReportsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    width: auto;
  }
`;

const ReportSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  color: #555;
  font-size: 1.1rem;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Table = styled.table`
  display: none;
  width: 100%;
  border-collapse: collapse;

  @media (min-width: 768px) {
    display: table;
  }
`;

const TableRow = styled.tr`
  background-color: ${props => (props.$active ? '#f1f8e9' : 'transparent')};
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

// Mobile Card Components
const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  background: ${props => props.$active ? '#f1f8e9' : '#f8f9fa'};
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid ${props => props.$active ? '#4CAF50' : '#ddd'};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const MobileCardTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
`;

const MobileCardLabel = styled.span`
  color: #666;
`;

const MobileCardValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const MobileCardActions = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #888;
  border: 1px dashed #ccc;
  border-radius: 8px;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: #1976d2;
  }

  &:disabled {
    background-color: #90caf9;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    width: auto;
    font-size: 0.9rem;
  }
`;

const Reports = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const normalizeFilters = (value = {}) => ({
    startDate: value.startDate || '',
    endDate: value.endDate || '',
    reportType: value.reportType || 'summary',
    clientId: value.clientId || ''
  });

  const [filters, setFilters] = useState(normalizeFilters({
    startDate: firstDayOfMonth.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
    reportType: 'summary'
  }));

  const [activeReportId, setActiveReportId] = useState(null);
  const [downloadingReportId, setDownloadingReportId] = useState(null);

  const queryClient = useQueryClient();

  const { data: historyData } = useQuery({
    queryKey: ['reports', 'history'],
    queryFn: api.getReportHistory
  });

  const historyReports = historyData?.reports || [];

  useEffect(() => {
    if (!activeReportId && historyReports.length > 0) {
      const initialReport = historyReports[0];
      const normalizedInitial = normalizeFilters(initialReport.filters || {});
      setActiveReportId(initialReport.id);
      setFilters(prev => {
        const next = {
          ...prev,
          startDate: normalizedInitial.startDate,
          endDate: normalizedInitial.endDate,
          reportType: normalizedInitial.reportType,
          clientId: normalizedInitial.clientId
        };
        const changed = Object.keys(next).some((key) => next[key] !== prev[key]);
        return changed ? next : prev;
      });
    }
  }, [historyReports, activeReportId]);

  const generateReportMutation = useMutation({
    mutationFn: (payload) => api.generateReport(payload),
    onSuccess: (response) => {
      if (response?.report) {
        queryClient.setQueryData(['reports', 'history'], (oldData) => {
          const previousReports = oldData?.reports || [];
          const filteredReports = previousReports.filter(r => r.id !== response.report.id);
          return {
            success: true,
            reports: [response.report, ...filteredReports]
          };
        });
        setActiveReportId(response.report.id);
        const normalizedNext = normalizeFilters(response.report.filters || {});
        setFilters(prev => ({
          ...prev,
          startDate: normalizedNext.startDate,
          endDate: normalizedNext.endDate,
          reportType: normalizedNext.reportType,
          clientId: normalizedNext.clientId
        }));
      }
    },
    onError: () => {
      toast.error('Error al generar el reporte');
    }
  });

  const isLoading = generateReportMutation.isPending;

  const activeReportEntry = useMemo(
    () => historyReports.find(report => report.id === activeReportId) || null,
    [historyReports, activeReportId]
  );

  const activeReportData = activeReportEntry?.data;
  const activeReportType = activeReportEntry?.filters?.reportType;

  const handleGenerateReport = () => {
    const payload = { ...filters };
    if (!payload.clientId) {
      delete payload.clientId;
    }
    generateReportMutation.mutate(payload);
  };

  const handleExportExcel = async () => {
    try {
      const blob = await api.exportReportExcel(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_${filters.startDate}_${filters.endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar el reporte');
    }
  };

  const handleViewReport = async (report) => {
    setActiveReportId(report.id);
    const normalizedSelected = normalizeFilters(report.filters || {});
    setFilters(prev => ({
      ...prev,
      startDate: normalizedSelected.startDate,
      endDate: normalizedSelected.endDate,
      reportType: normalizedSelected.reportType,
      clientId: normalizedSelected.clientId
    }));

    const sanitizeFilename = (value) => {
      return (value || 'reporte')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9-_]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
    };

    let downloadUrl;
    try {
      setDownloadingReportId(report.id);
      const blob = await api.getReportPdf(report.id);
      downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      const fileName = sanitizeFilename(
        `${report.name || 'reporte'}-${normalizedSelected.startDate || 'inicio'}-${normalizedSelected.endDate || 'hoy'}`
      );

      link.download = `${fileName || 'reporte'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Reporte descargado');
    } catch (error) {
      console.error('Report PDF download error', error);
      toast.error('No se pudo generar el PDF del reporte');
    } finally {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }
      setDownloadingReportId(null);
    }
  };

  const formatDisplayDate = (value) => (value ? formatDate(value) : 'Sin fecha');

  return (
    <ReportsContainer>
      <Title>Reportes</Title>

      <FilterSection>
        <FilterRow>
          <FormGroup>
            <Label>Fecha desde</Label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Fecha hasta</Label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Tipo de reporte</Label>
            <Select
              value={filters.reportType}
              onChange={(e) => setFilters({...filters, reportType: e.target.value})}
            >
              <option value="summary">Resumen general</option>
              <option value="sales">Ventas detalladas</option>
            </Select>
          </FormGroup>
        </FilterRow>
        
        <Button onClick={handleGenerateReport} disabled={isLoading}>
          {isLoading ? 'Generando...' : 'Generar Reporte'}
        </Button>
      </FilterSection>

      {isLoading && <LoadingState>Generando reporte...</LoadingState>}

      {activeReportData && (
        <>
          {activeReportType === 'sales' && activeReportData.salesDetails && (
            <ReportSection>
              <SectionTitle>Ventas Detalladas</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Fecha</TableHeader>
                    <TableHeader>Pedido</TableHeader>
                    <TableHeader>Cliente</TableHeader>
                    <TableHeader>Productos</TableHeader>
                    <TableHeader>Total</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {activeReportData.salesDetails.map((sale) => (
                    <tr key={`sale-${sale.orderNumber}`}>
                      <TableCell>{formatDisplayDate(sale.date)}</TableCell>
                      <TableCell>{sale.orderNumber}</TableCell>
                      <TableCell>{sale.clientName}</TableCell>
                      <TableCell>{sale.itemsCount} items</TableCell>
                      <TableCell>{formatCurrency(sale.total)}</TableCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              <Button onClick={handleExportExcel} style={{ marginTop: '1rem' }}>
                Exportar a Excel
              </Button>
            </ReportSection>
          )}

          {activeReportType === 'clients' && activeReportData.clientsReport && (
            <ReportSection>
              <SectionTitle>Reporte por Cliente</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Cliente</TableHeader>
                    <TableHeader>Pedidos</TableHeader>
                    <TableHeader>Total Comprado</TableHeader>
                    <TableHeader>Ticket Promedio</TableHeader>
                    <TableHeader>Última Compra</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {activeReportData.clientsReport.map((client) => (
                    <tr key={`client-${client.name}-${client.ordersCount}-${client.totalPurchased}`}>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.ordersCount}</TableCell>
                      <TableCell>{formatCurrency(client.totalPurchased)}</TableCell>
                      <TableCell>{formatCurrency(client.averageTicket)}</TableCell>
                      <TableCell>{formatDisplayDate(client.lastPurchase)}</TableCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              <Button onClick={handleExportExcel} style={{ marginTop: '1rem' }}>
                Exportar a Excel
              </Button>
            </ReportSection>
          )}

          {activeReportType === 'products' && activeReportData.productsReport && (
            <ReportSection>
              <SectionTitle>Reporte por Producto</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Producto</TableHeader>
                    <TableHeader>Categoría</TableHeader>
                    <TableHeader>Cantidad Vendida</TableHeader>
                    <TableHeader>Total Ventas</TableHeader>
                    <TableHeader>% del Total</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {activeReportData.productsReport.map((product) => (
                    <tr key={`product-${product.name}-${product.category}`}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>{product.quantitySold}</TableCell>
                      <TableCell>{formatCurrency(product.totalSales)}</TableCell>
                      <TableCell>{product.percentage.toFixed(1)}%</TableCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              <Button onClick={handleExportExcel} style={{ marginTop: '1rem' }}>
                Exportar a Excel
              </Button>
            </ReportSection>
          )}
        </>
      )}

      <ReportSection>
        <SectionTitle>Reportes generados</SectionTitle>
        {historyReports.length === 0 ? (
          <EmptyState>No hay reportes generados</EmptyState>
        ) : (
          <>
            {/* Mobile Card View */}
            <MobileCardList>
              {historyReports.map(report => (
                <MobileCard key={report.id} $active={report.id === activeReportId}>
                  <MobileCardHeader>
                    <MobileCardTitle>{report.name}</MobileCardTitle>
                  </MobileCardHeader>
                  <MobileCardBody>
                    <MobileCardRow>
                      <MobileCardLabel>Rango</MobileCardLabel>
                      <MobileCardValue>
                        {formatDisplayDate(report.filters?.startDate)} — {formatDisplayDate(report.filters?.endDate)}
                      </MobileCardValue>
                    </MobileCardRow>
                    <MobileCardRow>
                      <MobileCardLabel>Generado</MobileCardLabel>
                      <MobileCardValue>{formatDisplayDate(report.generatedAt)}</MobileCardValue>
                    </MobileCardRow>
                  </MobileCardBody>
                  <MobileCardActions>
                    <ActionButton
                      type="button"
                      onClick={() => handleViewReport(report)}
                      disabled={downloadingReportId === report.id}
                    >
                      {downloadingReportId === report.id ? 'Generando PDF...' : 'Ver reporte'}
                    </ActionButton>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCardList>

            {/* Desktop Table View */}
            <Table>
              <thead>
                <tr>
                  <TableHeader>Reporte</TableHeader>
                  <TableHeader>Rango de fechas</TableHeader>
                  <TableHeader>Generado</TableHeader>
                  <TableHeader>Acciones</TableHeader>
                </tr>
              </thead>
              <tbody>
                {historyReports.map(report => (
                  <TableRow key={report.id} $active={report.id === activeReportId}>
                    <TableCell>{report.name}</TableCell>
                    <TableCell>
                      {formatDisplayDate(report.filters?.startDate)} — {formatDisplayDate(report.filters?.endDate)}
                    </TableCell>
                    <TableCell>{formatDisplayDate(report.generatedAt)}</TableCell>
                    <TableCell>
                      <ActionButton
                        type="button"
                        onClick={() => handleViewReport(report)}
                        disabled={downloadingReportId === report.id}
                      >
                        {downloadingReportId === report.id ? 'Generando PDF...' : 'Ver reporte'}
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </ReportSection>
    </ReportsContainer>
  );
};

export default Reports;
